import { useAppDispatch } from 'app/store';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { RowRendererProps } from 'react-data-grid';
import { BodyRow } from 'app/components/ChakraTableList/types';
import { calculateIndex } from 'app/components/Releases/ReleaseVersionsList/utils';
import { debounce } from 'lodash';
import { DraggableListRow } from 'app/components/DraggableListRow';
import { getReorderBody } from 'app/components/DraggableListRow/utils';
import { ReorderBody } from 'app/components/DraggableListRow/types';

interface DraggableRow {
  guid: string;
  tooltip: string;
}

interface UseRowRendererProps<Row> {
  setRows: Dispatch<SetStateAction<Row[]>>;
  rows: Array<Row & DraggableRow>;
  onReorder: (data: ReorderBody, reordered: Row[]) => Promise<boolean>;
}

export const useDraggableRowRenderer = <Row extends {}>({
  rows,
  setRows,
  onReorder,
}: UseRowRendererProps<Row>) => {
  const dispatch = useAppDispatch();
  const [tooltipTitle, setTooltipTitle] = useState<string | undefined>('');

  const onRowReorder = async (
    fromIndex: number,
    toIndex: number,
    isBorderTop: boolean
  ) => {
    setTooltipTitle('');
    const actualIndex = calculateIndex(
      fromIndex,
      toIndex,
      rows.length,
      isBorderTop
    );

    if (actualIndex !== fromIndex) {
      let index;
      if (actualIndex === 0 || actualIndex === rows.length - 1) {
        index = actualIndex;
      } else if (fromIndex < toIndex) {
        index = actualIndex;
      } else {
        index = actualIndex - 1;
      }

      const requestBody = getReorderBody(
        rows[fromIndex].guid,
        rows[index].guid,
        actualIndex !== 0
      );

      const newRows = [...rows];
      newRows.splice(actualIndex, 0, newRows.splice(fromIndex, 1)[0]);

      const isSuccess = await onReorder(requestBody, newRows);
      if (isSuccess) {
        setRows(newRows);
      }
    }
  };

  const onDragging = debounce(
    (rowIndex: number) => {
      setTooltipTitle(rows[rowIndex].tooltip);
    },
    400,
    { leading: true }
  );

  const RowRenderer = useCallback(
    (props: RowRendererProps<BodyRow>) => {
      return (
        <DraggableListRow
          {...props}
          onRowReorder={onRowReorder}
          onHover={onDragging}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, rows]
  );

  return { RowRenderer, tooltipTitle, onRowReorder, onDragging };
};
